'use client';

import { useEffect, useState } from 'react';

import { useUserSession } from './user-session';

import { isRecord } from '@/lib/athena-utils';
import { opendoorUrl } from '@/lib/urls';

export type SessionExtras = {
  hasResaleClosingDashboard: boolean;
};

const SESSION_INFO_URL = opendoorUrl('/session/info');

// Keyed by customerUuid because /session/info's identity rides on the
// session cookie rather than a request param; the uuid is the stable
// per-customer cache key, not a request argument.
const cache = new Map<string, SessionExtras>();
const inflight = new Map<string, Promise<SessionExtras>>();

async function fetchSessionExtras(cacheKey: string): Promise<SessionExtras> {
  try {
    const response = await fetch(SESSION_INFO_URL, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return { hasResaleClosingDashboard: false };
    const payload: unknown = await response.json();
    if (!isRecord(payload)) return { hasResaleClosingDashboard: false };
    const human = payload['human'];
    const flag = isRecord(human) ? human['has_resale_closing_dashboard'] : undefined;
    const result = { hasResaleClosingDashboard: flag === true };
    cache.set(cacheKey, result);
    return result;
  } catch {
    return { hasResaleClosingDashboard: false };
  }
}

export function useSessionExtras(): SessionExtras {
  const auth = useUserSession();
  const customerUuid = auth.kind === 'authenticated' ? auth.human.customerUuid : undefined;
  const [extras, setExtras] = useState<SessionExtras>(() =>
    customerUuid
      ? (cache.get(customerUuid) ?? { hasResaleClosingDashboard: false })
      : { hasResaleClosingDashboard: false },
  );

  useEffect(() => {
    if (!customerUuid) {
      setExtras({ hasResaleClosingDashboard: false });
      return;
    }
    const cached = cache.get(customerUuid);
    if (cached) {
      setExtras(cached);
      return;
    }
    let cancelled = false;
    let request = inflight.get(customerUuid);
    if (!request) {
      request = fetchSessionExtras(customerUuid).then((result) => {
        inflight.delete(customerUuid);
        return result;
      });
      inflight.set(customerUuid, request);
    }
    request.then((result) => {
      if (!cancelled) setExtras(result);
    });
    return () => {
      cancelled = true;
    };
  }, [customerUuid]);

  return extras;
}
